import React, { useState } from 'react';
import styled from 'styled-components';

const MobileGamesContainer = styled.div`
  padding: 15px;
  background: rgba(0, 0, 0, 0.95);
  color: #00ff00;
  min-height: 100vh;
  font-family: 'Ubuntu', sans-serif;
`;

const GameSelector = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  overflow-x: auto;
  padding-bottom: 10px;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 255, 0, 0.5);
    border-radius: 2px;
  }
`;

const GameButton = styled.button<{ active: boolean }>`
  background: ${props => props.active ? 'rgba(0, 255, 0, 0.3)' : 'rgba(0, 255, 0, 0.1)'};
  border: 1px solid ${props => props.active ? '#00ff00' : 'rgba(0, 255, 0, 0.3)'};
  color: #00ff00;
  padding: 10px 15px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
  min-width: 80px;
  
  &:active {
    transform: scale(0.95);
  }
`;

const GameArea = styled.div`
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  min-height: 400px;
`;

// Mobile Tic-Tac-Toe
const MobileGameBoard = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  max-width: 280px;
  margin: 20px auto;
`;

const MobileCell = styled.button<{ value: string }>`
  width: 80px;
  height: 80px;
  background: rgba(0, 0, 0, 0.8);
  border: 2px solid #00ff00;
  color: ${props => props.value === 'X' ? '#00ff00' : '#ff0000'};
  font-size: 28px;
  font-weight: bold;
  cursor: pointer;
  border-radius: 10px;
  transition: all 0.2s ease;
  
  &:active {
    transform: scale(0.9);
    background: rgba(0, 255, 0, 0.1);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

// Mobile Password Game
const MobilePasswordInput = styled.input`
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid #00ff00;
  color: #00ff00;
  padding: 15px;
  font-size: 18px;
  text-align: center;
  border-radius: 10px;
  margin: 20px;
  width: calc(100% - 40px);
  max-width: 300px;
  font-family: 'Courier New', monospace;

  &:focus {
    outline: none;
    box-shadow: 0 0 15px rgba(0, 255, 0, 0.5);
  }
`;

// Mobile Network Scanner
const MobileNetworkGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  max-width: 300px;
  margin: 20px auto;
`;

const MobileNetworkNode = styled.button<{ scanned: boolean; vulnerable: boolean }>`
  width: 60px;
  height: 60px;
  background: ${props => 
    props.vulnerable && props.scanned ? 'rgba(255, 0, 0, 0.3)' :
    props.scanned ? 'rgba(0, 255, 0, 0.3)' : 
    'rgba(0, 0, 0, 0.8)'
  };
  border: 2px solid ${props => 
    props.vulnerable && props.scanned ? '#ff0000' :
    props.scanned ? '#00ff00' : 
    'rgba(0, 255, 0, 0.3)'
  };
  color: #ffffff;
  cursor: pointer;
  border-radius: 10px;
  font-size: 20px;
  
  &:active {
    transform: scale(0.9);
  }
`;

const MobileActionButton = styled.button`
  background: rgba(0, 255, 0, 0.2);
  border: 2px solid #00ff00;
  color: #00ff00;
  padding: 15px 25px;
  border-radius: 25px;
  cursor: pointer;
  margin: 10px;
  font-weight: bold;
  font-size: 14px;
  min-width: 120px;
  
  &:active {
    background: rgba(0, 255, 0, 0.3);
    transform: scale(0.95);
  }
`;

const MobileHackerGames: React.FC = () => {
  const [currentGame, setCurrentGame] = useState('tictactoe');
  const [board, setBoard] = useState(Array(9).fill(''));
  const [isXNext, setIsXNext] = useState(true);
  const [password] = useState('CYBER');
  const [guess, setGuess] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [cracked, setCracked] = useState(false);
  const [networkNodes, setNetworkNodes] = useState<Array<{scanned: boolean; vulnerable: boolean}>>([]);
  const [vulnerabilitiesFound, setVulnerabilitiesFound] = useState(0);

  const calculateWinner = (squares: string[]) => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8],
      [0, 3, 6], [1, 4, 7], [2, 5, 8],
      [0, 4, 8], [2, 4, 6]
    ];
    
    for (let line of lines) {
      const [a, b, c] = line;
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return squares[a];
      }
    }
    return null;
  };

  const handleCellClick = (index: number) => {
    if (board[index] || calculateWinner(board)) return;
    
    const newBoard = [...board];
    newBoard[index] = isXNext ? 'X' : 'O';
    setBoard(newBoard);
    setIsXNext(!isXNext);
  };

  const resetTicTacToe = () => {
    setBoard(Array(9).fill(''));
    setIsXNext(true);
  };

  const handlePasswordCrack = () => {
    setAttempts(prev => prev + 1);
    if (guess.toUpperCase() === password) {
      setCracked(true);
    }
  };

  const resetPasswordGame = () => {
    setGuess('');
    setAttempts(0);
    setCracked(false);
  };

  const initializeNetwork = () => {
    const nodes = Array(16).fill(null).map(() => ({
      scanned: false,
      vulnerable: Math.random() > 0.7
    }));
    setNetworkNodes(nodes);
    setVulnerabilitiesFound(0);
  };

  const scanNode = (index: number) => {
    if (networkNodes[index]?.scanned) return;
    
    const newNodes = [...networkNodes];
    newNodes[index].scanned = true;
    setNetworkNodes(newNodes);
    
    if (newNodes[index].vulnerable) {
      setVulnerabilitiesFound(prev => prev + 1);
    }
  };

  React.useEffect(() => {
    if (currentGame === 'network' && networkNodes.length === 0) {
      initializeNetwork();
    }
  }, [currentGame]);

  const renderTicTacToe = () => {
    const winner = calculateWinner(board);
    return (
      <div>
        <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>🎮 Tic-Tac-Toe</h2>
        <p style={{ color: '#00ffff', fontSize: '14px', marginBottom: '15px' }}>
          {winner ? `🏆 Winner: ${winner}` : `Next: ${isXNext ? 'X' : 'O'}`}
        </p>
        <MobileGameBoard>
          {board.map((cell, index) => (
            <MobileCell
              key={index}
              value={cell}
              onClick={() => handleCellClick(index)}
              disabled={!!cell || !!winner}
            >
              {cell}
            </MobileCell>
          ))}
        </MobileGameBoard>
        <MobileActionButton onClick={resetTicTacToe}>
          🔄 New Game
        </MobileActionButton>
      </div>
    );
  };

  const renderPasswordCracking = () => (
    <div>
      <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>🔐 Password Crack</h2>
      <p style={{ color: '#ffff00', fontSize: '14px' }}>Crack the {password.length}-letter password!</p>
      <p style={{ color: '#00ffff', fontSize: '12px', marginBottom: '15px' }}>Hint: Cybersecurity term</p>
      <MobilePasswordInput
        value={guess}
        onChange={(e) => setGuess(e.target.value)}
        maxLength={password.length}
        placeholder="Enter password..."
        onKeyPress={(e) => e.key === 'Enter' && handlePasswordCrack()}
      />
      <br />
      <MobileActionButton onClick={handlePasswordCrack}>
        🚀 Crack It
      </MobileActionButton>
      <MobileActionButton onClick={resetPasswordGame}>
        🎲 Reset
      </MobileActionButton>
      <p style={{ color: '#cccccc', fontSize: '12px', marginTop: '10px' }}>
        Attempts: {attempts}
      </p>
      {cracked && (
        <div style={{ color: '#00ff00', fontSize: '16px', marginTop: '15px' }}>
          🎉 ACCESS GRANTED! 🎉<br />
          Password: {password}
        </div>
      )}
    </div>
  );

  const renderNetworkScanner = () => (
    <div>
      <h2 style={{ fontSize: '18px', marginBottom: '15px' }}>🌐 Network Scanner</h2>
      <p style={{ color: '#00ffff', fontSize: '14px' }}>Find vulnerabilities!</p>
      <p style={{ color: '#cccccc', fontSize: '12px', marginBottom: '15px' }}>
        Found: {vulnerabilitiesFound} vulnerabilities
      </p>
      <MobileNetworkGrid>
        {networkNodes.map((node, index) => (
          <MobileNetworkNode
            key={index}
            scanned={node.scanned}
            vulnerable={node.vulnerable}
            onClick={() => scanNode(index)}
          >
            {node.scanned ? (node.vulnerable ? '🚨' : '✅') : '❓'}
          </MobileNetworkNode>
        ))}
      </MobileNetworkGrid>
      <MobileActionButton onClick={initializeNetwork}>
        🔄 New Network
      </MobileActionButton>
    </div>
  );

  return (
    <MobileGamesContainer>
      <h1 style={{ textAlign: 'center', color: '#00ffff', marginBottom: '20px', fontSize: '20px' }}>
        🎮 Hacker Games
      </h1>
      
      <GameSelector>
        <GameButton 
          active={currentGame === 'tictactoe'} 
          onClick={() => setCurrentGame('tictactoe')}
        >
          🎯 Tic-Tac-Toe
        </GameButton>
        <GameButton 
          active={currentGame === 'password'} 
          onClick={() => setCurrentGame('password')}
        >
          🔐 Password
        </GameButton>
        <GameButton 
          active={currentGame === 'network'} 
          onClick={() => setCurrentGame('network')}
        >
          🌐 Network
        </GameButton>
      </GameSelector>

      <GameArea>
        {currentGame === 'tictactoe' && renderTicTacToe()}
        {currentGame === 'password' && renderPasswordCracking()}
        {currentGame === 'network' && renderNetworkScanner()}
      </GameArea>
    </MobileGamesContainer>
  );
};

export default MobileHackerGames;
